import { useMemo } from 'react'
import {
  ArrayParam,
  BooleanParam,
  DelimitedArrayParam,
  DelimitedNumericArrayParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params'

import { QueryParameter } from '@/modules/shared/types/QueryParameter'
import changeDateForQuery from '@/modules/shared/utils/changeDateForQuery'

export interface QueryValues {
  searchValue: string
  status?: string
  selfManaged?: string
  enabled?: string
  fromDate?: string
  toDate?: string
  supplierIds?: (number | null)[] | null
  matchedInvoice?: string | null
  completedReceivingNote?: string | null
  periodYear?: number | null
  periodMonth?: number | null
  categoryIds?: (number | null)[] | null
  bestBuy?: boolean
  supplierProductCode?: string | null
  purchaserProductCode?: string | null
  pPlusProductCode?: string | null
  stockLocations?: (string | null)[] | null
  originStockLocation?: number | null
  destinationStockLocation?: number | null
  countStatus?: (string | null)[] | null
  hideZero?: boolean
  hasQuotes?: boolean | null
  masterProduct?: boolean | null
  hasImage?: boolean | null
  catalogued?: boolean | null
  matched?: boolean | null
  countedItems?: boolean | null
  requisitionType?: string | null
  departmentIds?: (number | null)[] | null
  accountIds?: (number | null)[] | null
  destinationIds?: (number | null)[] | null
  ownerIds?: (number | null)[] | null
  organisationUnitIds?: (number | null)[] | null
  preferred?: boolean | null
  id?: number | null
  tradeRelationshipId?: number | null
  apSystemId?: number | null
  purchaserId?: number | null
  supplierId?: number | null
  ownerId?: number | null
  ownerName?: string | null
  verified?: boolean | null
  legalEntity?: boolean | null
  barcode?: string | null
  transferTypes?: (string | null)[] | null
  catalogFilter?: string | null
  alreadyReceived?: boolean
  pplusCode?: string | null
  organisationId?: string | null
  invoiceNumber?: string | null
  creditNoteNumber?: string | null
  catalogId?: number | null
  tradeRelationshipPurchaserId?: number | null
  tradeRelationshipSupplierId?: number | null
  catalogOwnerId?: number | null
  catalogSupplierId?: number | null
  smCatalog?: boolean | null
  contracted?: boolean | null
  common?: boolean | null
  documentType?: string | null
  workflowType?: string | null
  customCodeEnabled?: boolean | null
  markedAs?: string | null
  from?: string | null
  to?: string | null
  subjectLine?: string | null
  businessNumber?: string | null
  documentNumber?: string | null
  userId?: string | null
  email?: string | null
  reference?: string | null
  paymentStatus?: string | null
  accountType?: boolean | null
  externalId?: string | null
  ghostLines?: boolean | null
  isRecipe?: boolean | null
  type?: string | null
  unitSize?: number | null
  unitMeasure?: string | null
  sellQuantity?: number | null
  sellPackName?: string | null
  showErrorState?: boolean | null
  createdDocument?: string | null
  supplierOrganisationId?: number | null
  purchaserOrganisationId?: number | null
  hasPurchaseOrder?: boolean | null
  hasReceivingNotes?: boolean | null
  hasOperationalApproval?: boolean | null
  productId?: number | null
  buyListId?: number | null
  structurallySimilar?: boolean | null
  toEmail?: string | null
  ccEmail?: string | null
  connected?: boolean | null
  fromEmail?: string | null
  paperlessMatchString?: string | null
  requisitionStatus?: string | null
  supplierNumber?: string | null
  processedAs?: (string | null)[] | null
  invoiceAge?: string | null
  hasSupplier?: boolean | null
  reportCategory?: (string | null)[] | null
  reportType?: (string | null)[] | null
  scanningTaskId?: number | null
  scanningTaskNumber?: string | null
  productTags?: (number | null)[] | null
  organisationTags?: (number | null)[] | null
  suppliers?: (number | null)[] | null
  purchaserName?: string | null
}

/**
 * Listen to the query parameters.
 *
 * @returns {QueryValues} An object containing the changed query params with its new value.
 */
export function useWatchQueryParams(): QueryValues {
  const [query] = useQueryParams({
    [QueryParameter.Search]: StringParam,
    [QueryParameter.Status]: withDefault(StringParam, 'all'),
    [QueryParameter.SelfManaged]: withDefault(StringParam, 'all'),
    [QueryParameter.Enabled]: withDefault(StringParam, 'all'),
    [QueryParameter.From]: StringParam,
    [QueryParameter.To]: StringParam,
    [QueryParameter.Suppliers]: DelimitedNumericArrayParam,
    [QueryParameter.MatchedInvoice]: withDefault(StringParam, 'all'),
    [QueryParameter.CompletedReceivingNote]: withDefault(StringParam, 'all'),
    [QueryParameter.RequisitionType]: withDefault(StringParam, 'all'),
    [QueryParameter.PeriodYear]: NumberParam,
    [QueryParameter.PeriodMonth]: NumberParam,
    [QueryParameter.Categories]: DelimitedNumericArrayParam,
    [QueryParameter.BestBuy]: BooleanParam,
    [QueryParameter.SupplierProductCode]: StringParam,
    [QueryParameter.PurchaserProductCode]: StringParam,
    [QueryParameter.PPlusProductCode]: StringParam,
    [QueryParameter.StockLocations]: DelimitedArrayParam,
    [QueryParameter.OriginStockLocation]: NumberParam,
    [QueryParameter.DestinationStockLocation]: NumberParam,
    [QueryParameter.CountStatus]: DelimitedArrayParam,
    [QueryParameter.HideZero]: BooleanParam,
    [QueryParameter.HasQuotes]: BooleanParam,
    [QueryParameter.MasterProduct]: BooleanParam,
    [QueryParameter.Image]: BooleanParam,
    [QueryParameter.Catalogued]: BooleanParam,
    [QueryParameter.Matched]: BooleanParam,
    [QueryParameter.CountedItems]: BooleanParam,
    [QueryParameter.Preferred]: BooleanParam,
    [QueryParameter.Departments]: DelimitedNumericArrayParam,
    [QueryParameter.Accounts]: DelimitedNumericArrayParam,
    [QueryParameter.Destination]: DelimitedNumericArrayParam,
    [QueryParameter.Owner]: DelimitedNumericArrayParam,
    [QueryParameter.OrganisationUnit]: DelimitedNumericArrayParam,
    [QueryParameter.Id]: NumberParam,
    [QueryParameter.TradeRelationshipId]: NumberParam,
    [QueryParameter.ApSystemId]: NumberParam,
    [QueryParameter.PurchaserId]: NumberParam,
    [QueryParameter.SupplierId]: NumberParam,
    [QueryParameter.OwnerId]: NumberParam,
    [QueryParameter.OwnerName]: StringParam,
    [QueryParameter.Verified]: BooleanParam,
    [QueryParameter.LegalEntity]: BooleanParam,
    [QueryParameter.Barcode]: StringParam,
    [QueryParameter.TransferTypes]: DelimitedArrayParam,
    [QueryParameter.CatalogFilter]: StringParam,
    [QueryParameter.AlreadyReceived]: BooleanParam,
    [QueryParameter.PPlusCode]: StringParam,
    [QueryParameter.OrganisationId]: StringParam,
    [QueryParameter.InvoiceNumber]: StringParam,
    [QueryParameter.CreditNoteNumber]: StringParam,
    [QueryParameter.CatalogId]: NumberParam,
    [QueryParameter.TradeRelationshipPurchaserId]: NumberParam,
    [QueryParameter.TradeRelationshipSupplierId]: NumberParam,
    [QueryParameter.CatalogOwnerId]: NumberParam,
    [QueryParameter.CatalogSupplierId]: NumberParam,
    [QueryParameter.SMCatalog]: BooleanParam,
    [QueryParameter.Contracted]: BooleanParam,
    [QueryParameter.Common]: BooleanParam,
    [QueryParameter.CustomCodeEnabled]: BooleanParam,
    [QueryParameter.DocumentType]: StringParam,
    [QueryParameter.WorkflowType]: StringParam,
    [QueryParameter.MarkedAs]: StringParam,
    [QueryParameter.SubjectLine]: StringParam,
    [QueryParameter.BusinessNumber]: StringParam,
    [QueryParameter.DocumentNumber]: StringParam,
    [QueryParameter.UserId]: StringParam,
    [QueryParameter.Email]: StringParam,
    [QueryParameter.Reference]: StringParam,
    [QueryParameter.PaymentStatus]: StringParam,
    [QueryParameter.AccountType]: BooleanParam,
    [QueryParameter.ExternalId]: StringParam,
    [QueryParameter.GhostLines]: BooleanParam,
    [QueryParameter.IsRecipe]: BooleanParam,
    [QueryParameter.Type]: StringParam,
    [QueryParameter.UnitSize]: NumberParam,
    [QueryParameter.UnitMeasure]: StringParam,
    [QueryParameter.SellQuantity]: NumberParam,
    [QueryParameter.SellPackName]: StringParam,
    [QueryParameter.ShowErrors]: BooleanParam,
    [QueryParameter.CreatedDocument]: StringParam,
    [QueryParameter.SupplierOrganisationId]: NumberParam,
    [QueryParameter.PurchaserOrganisationId]: NumberParam,
    [QueryParameter.HasPurchaseOrder]: BooleanParam,
    [QueryParameter.HasReceivingNotes]: BooleanParam,
    [QueryParameter.HasOperationalApproval]: BooleanParam,
    [QueryParameter.ProductId]: NumberParam,
    [QueryParameter.BuyListId]: NumberParam,
    [QueryParameter.StructurallySimilar]: BooleanParam,
    [QueryParameter.ToEmail]: StringParam,
    [QueryParameter.CcEmail]: StringParam,
    [QueryParameter.Connected]: BooleanParam,
    [QueryParameter.FromEmail]: StringParam,
    [QueryParameter.PaperlessMatchString]: StringParam,
    [QueryParameter.RequisitionStatus]: StringParam,
    [QueryParameter.SupplierNumber]: StringParam,
    [QueryParameter.ProcessedAs]: withDefault(ArrayParam, [] as (string | null)[]),
    [QueryParameter.InvoiceAge]: StringParam,
    [QueryParameter.HasSupplier]: BooleanParam,
    [QueryParameter.ReportCategory]: DelimitedArrayParam,
    [QueryParameter.ReportType]: DelimitedArrayParam,
    [QueryParameter.ScanningTaskId]: NumberParam,
    [QueryParameter.ScanningTaskNumber]: StringParam,
    [QueryParameter.ProductTags]: DelimitedNumericArrayParam,
    [QueryParameter.OrganisationTags]: DelimitedNumericArrayParam,
    [QueryParameter.PurchaserName]: StringParam,
  })

  const data: QueryValues = useMemo(
    () => ({
      searchValue: query[QueryParameter.Search] || '',
      status: query[QueryParameter.Status],
      selfManaged: query[QueryParameter.SelfManaged],
      enabled: query[QueryParameter.Enabled],
      fromDate: changeDateForQuery(query[QueryParameter.From]),
      toDate: changeDateForQuery(query[QueryParameter.To]),
      supplierIds: query[QueryParameter.Suppliers],
      matchedInvoice: query[QueryParameter.MatchedInvoice],
      completedReceivingNote: query[QueryParameter.CompletedReceivingNote],
      periodYear: query[QueryParameter.PeriodYear],
      periodMonth: query[QueryParameter.PeriodMonth],
      categoryIds: query[QueryParameter.Categories],
      bestBuy: !!query[QueryParameter.BestBuy],
      supplierProductCode: query[QueryParameter.SupplierProductCode],
      purchaserProductCode: query[QueryParameter.PurchaserProductCode],
      pPlusProductCode: query[QueryParameter.PPlusProductCode],
      stockLocations: query[QueryParameter.StockLocations],
      originStockLocation: query[QueryParameter.OriginStockLocation],
      destinationStockLocation: query[QueryParameter.DestinationStockLocation],
      countStatus: query[QueryParameter.CountStatus],
      hideZero: !!query[QueryParameter.HideZero],
      hasQuotes: query[QueryParameter.HasQuotes],
      masterProduct: query[QueryParameter.MasterProduct],
      hasImage: query[QueryParameter.Image],
      catalogued: query[QueryParameter.Catalogued],
      matched: query[QueryParameter.Matched],
      countedItems: query[QueryParameter.CountedItems],
      requisitionType: query[QueryParameter.RequisitionType],
      departmentIds: query[QueryParameter.Departments],
      accountIds: query[QueryParameter.Accounts],
      destinationIds: query[QueryParameter.Destination],
      ownerIds: query[QueryParameter.Owner],
      organisationUnitIds: query[QueryParameter.OrganisationUnit],
      preferred: query[QueryParameter.Preferred],
      id: query[QueryParameter.Id],
      tradeRelationshipId: query[QueryParameter.TradeRelationshipId],
      apSystemId: query[QueryParameter.ApSystemId],
      purchaserId: query[QueryParameter.PurchaserId],
      supplierId: query[QueryParameter.SupplierId],
      ownerId: query[QueryParameter.OwnerId],
      ownerName: query[QueryParameter.OwnerName],
      verified: query[QueryParameter.Verified],
      legalEntity: query[QueryParameter.LegalEntity],
      barcode: query[QueryParameter.Barcode],
      transferTypes: query[QueryParameter.TransferTypes],
      catalogFilter: query[QueryParameter.CatalogFilter],
      alreadyReceived: !!query[QueryParameter.AlreadyReceived],
      pplusCode: query[QueryParameter.PPlusCode],
      organisationId: query[QueryParameter.OrganisationId],
      invoiceNumber: query[QueryParameter.InvoiceNumber],
      creditNoteNumber: query[QueryParameter.CreditNoteNumber],
      catalogId: query[QueryParameter.CatalogId],
      tradeRelationshipPurchaserId: query[QueryParameter.TradeRelationshipPurchaserId],
      tradeRelationshipSupplierId: query[QueryParameter.TradeRelationshipSupplierId],
      catalogOwnerId: query[QueryParameter.CatalogOwnerId],
      catalogSupplierId: query[QueryParameter.CatalogSupplierId],
      smCatalog: query[QueryParameter.SMCatalog],
      contracted: query[QueryParameter.Contracted],
      common: query[QueryParameter.Common],
      documentType: query[QueryParameter.DocumentType],
      customCodeEnabled: query[QueryParameter.CustomCodeEnabled],
      workflowType: query[QueryParameter.WorkflowType],
      markedAs: query[QueryParameter.MarkedAs],
      from: query[QueryParameter.From],
      to: query[QueryParameter.To],
      subjectLine: query[QueryParameter.SubjectLine],
      businessNumber: query[QueryParameter.BusinessNumber],
      documentNumber: query[QueryParameter.DocumentNumber],
      userId: query[QueryParameter.UserId],
      email: query[QueryParameter.Email],
      reference: query[QueryParameter.Reference],
      paymentStatus: query[QueryParameter.PaymentStatus],
      accountType: query[QueryParameter.AccountType],
      externalId: query[QueryParameter.ExternalId],
      ghostLines: query[QueryParameter.GhostLines],
      isRecipe: query[QueryParameter.IsRecipe],
      type: query[QueryParameter.Type],
      unitSize: query[QueryParameter.UnitSize],
      unitMeasure: query[QueryParameter.UnitMeasure],
      sellQuantity: query[QueryParameter.SellQuantity],
      sellPackName: query[QueryParameter.SellPackName],
      showErrorState: query[QueryParameter.ShowErrors],
      createdDocument: query[QueryParameter.CreatedDocument],
      supplierOrganisationId: query[QueryParameter.SupplierOrganisationId],
      purchaserOrganisationId: query[QueryParameter.PurchaserOrganisationId],
      hasPurchaseOrder: query[QueryParameter.HasPurchaseOrder],
      hasReceivingNotes: query[QueryParameter.HasReceivingNotes],
      hasOperationalApproval: query[QueryParameter.HasOperationalApproval],
      productId: query[QueryParameter.ProductId],
      buyListId: query[QueryParameter.BuyListId],
      structurallySimilar: query[QueryParameter.StructurallySimilar],
      toEmail: query[QueryParameter.ToEmail],
      ccEmail: query[QueryParameter.CcEmail],
      connected: query[QueryParameter.Connected],
      fromEmail: query[QueryParameter.FromEmail],
      paperlessMatchString: query[QueryParameter.PaperlessMatchString],
      requisitionStatus: query[QueryParameter.RequisitionStatus],
      supplierNumber: query[QueryParameter.SupplierNumber],
      processedAs: query[QueryParameter.ProcessedAs],
      invoiceAge: query[QueryParameter.InvoiceAge],
      hasSupplier: query[QueryParameter.HasSupplier],
      reportCategory: query[QueryParameter.ReportCategory],
      reportType: query[QueryParameter.ReportType],
      scanningTaskId: query[QueryParameter.ScanningTaskId],
      scanningTaskNumber: query[QueryParameter.ScanningTaskNumber],
      productTags: query[QueryParameter.ProductTags],
      organisationTags: query[QueryParameter.OrganisationTags],
      suppliers: query[QueryParameter.Suppliers],
      purchaserName: query[QueryParameter.PurchaserName],
    }),
    [query]
  )

  return data
}
